import { useAppStore } from "@/stores/appStore";
import { createFileRoute } from "@tanstack/react-router";
import styled from "styled-components";
import { type_c, type_a } from "@/theme/typography";
import Shapes from "@/components/primitives/Shapes/Shapes";
import ImageShapes from "@/components/primitives/ImageShapes/ImageShapes";
import { useContent } from "@/hooks/useContent";
import { useEffect } from "react";
import ItemSelector from "@/components/ui/Interactive/ItemSelector/ItemSelector";
import Button from "@/components/primitives/Button/Button";
import { GateProps, ItemSelectorProps, GateStateProps } from "@/services/interfaces";

export const Route = createFileRoute("/_configurator/step-1")({
	component: Step1,
});

const setStateValue = (stateKey: string, value: any) => {
	const state = (prevState: any) => ({
		...prevState.configurator,
		[stateKey]: {
			...prevState.configurator[stateKey], // Preserve existing nested values
			...value,
		},
	});

	useAppStore.setState((prevState) => ({
		configurator: state(prevState),
	}));
};

function Step1() {
	const { configurator } = useAppStore((state) => state);
	const { getCategory, getGates, getPillars } = useContent();

	const currentGate = getGates().find((x) => x.id === configurator.gate.id);
	const defaultPillar = getPillars().find((x) => x.id === configurator.gate.pillars[0]);
	const selectedCategory = getCategory().find((x) => x.id === configurator.category.id);
	const filteredGates = getGates().filter((gate) => selectedCategory?.gates.includes(gate.id)) as GateProps[];
	const availablePillars = getPillars().filter((pillar) => currentGate?.pillars.includes(pillar.id));

	useEffect(() => {
		setStateValue("gate", currentGate);

		if (!currentGate?.pillars.includes(configurator.pillar.id)) {
			setStateValue("pillar", defaultPillar);
		}
	}, [currentGate]);

	return (
		<Wrapper>
			<p className="step__number" style={type_c}>
				Step 1
			</p>
			<h4 className="step__title" style={type_a}>
				Shape
			</h4>
			<div className="section_wrapper">
				<Section>
					<p className="step__subtitle" style={type_c}>
						Category
					</p>
					<Shapes items={getCategory()} stateKey={"category"} type={"id"} defaultVal={selectedCategory} />
				</Section>

				<Section>
					<p className="step__subtitle" style={type_c}>
						Gates
					</p>
					<Shapes items={filteredGates} stateKey={"gate"} type={"id"} defaultVal={configurator.gate} />
				</Section>

				<Section>
					<p className="step__subtitle" style={type_c}>
						Pillars
					</p>
					<ImageShapes items={availablePillars} stateKey={"pillar"} type={"id"} defaultVal={configurator.pillar} />
				</Section>
			</div>
		</Wrapper>
	);
}

const Wrapper = styled.div`
	padding-block: 1rem;
	padding-inline: 10px;
	height: 100%;

	.section_wrapper {
		overflow: scroll;
		padding-bottom: 80px;
		height: 100%;
		padding-block-start: 0.5rem;
	}

	& > :last-child {
		margin-bottom: 0;
	}

	.step__number {
		color: #7d6a64;
	}

	.step__title {
		padding-block-end: 0.5rem;
		color: ${({ theme }) => theme.colors.secondary};
		border-bottom: 1px solid #e7dad7;
	}

	.step__subtitle {
		margin-bottom: 0.3rem;
		font-weight: 400;
		color: #7d6a64;
		margin-bottom: 1rem;
	}
`;

const Section = styled.section`
	margin-bottom: 1rem;
`;

const GridWrapper = styled.div<{ columns?: number }>`
	display: grid;
	grid-template-columns: repeat(${({ columns }) => columns || 2}, minMax(0, 1fr));
	row-gap: 0.5rem;
	column-gap: 8px;

	& > * {
		width: 100%;
		background: #e9e1de;
		padding-inline: 16px;
		padding-block: 0.8rem;
		border-radius: 5px;
	}
`;
